// Root application component.
// Providers (QueryClient, AntD ConfigProvider) + router.
// ErrorBoundary wraps everything to catch unexpected render errors.
// TODO F32: Add @tanstack/react-query-devtools in development mode.

import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import { RouterProvider } from 'react-router-dom';
import router from './router';
import ErrorBoundary from './shared/components/common/ErrorBoundary';
import { useAntdTheme, useThemeStore } from './stores/themeStore';
import AuthInitializer from './features/auth/components/AuthInitializer';
import { paletteCss } from './shared/theme/palette';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// CSS variables for everything outside AntD (glass.css, CSS Modules, charts, SVG diagrams):
// the `--cg-*` palette for both modes, generated from src/shared/theme/palette.ts (task 355.3).
// Injected once into <head> as a <style> tag. (Replaces the old --bg-primary/--color-accent set,
// which nothing read.)
const THEME_VARS_CSS = paletteCss();

// Injects CSS variables stylesheet once and syncs data-theme attribute on <html>.
function ThemeSync(): null {
  const { darkMode } = useThemeStore();

  // Inject stylesheet once on mount.
  React.useEffect(() => {
    const id = 'bancorix-theme-vars';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = THEME_VARS_CSS;
      document.head.appendChild(style);
    }
  }, []);

  // Sync data-theme attribute whenever darkMode changes.
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  return null;
}

// Separate component so useAntdTheme() (a hook) can run inside the provider tree.
function ThemedApp(): React.ReactElement {
  const antdTheme = useAntdTheme();
  return (
    <ConfigProvider theme={antdTheme}>
      <ThemeSync />
      <AuthInitializer>
        <RouterProvider router={router} />
      </AuthInitializer>
    </ConfigProvider>
  );
}

export default function App(): React.ReactElement {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemedApp />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
